import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Table, { TableSize } from '@components/Table';
import { taxItemPath, toggleActiveTaxItemPath } from 'routes';
import api from '@utils/axios';
import { translate } from '@utils/i18n';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import ActionButtons from './ActionButtons';
import ExpandableRow from './ExpandableRow';
import SearchPanel from '../../../../components/SearchPanel';
import CreateTaxItem from './CreateTaxItem';
import EditTaxRate from './EditTaxRate';
import EditTaxGroup from './EditTaxGroup';
import { updateItems } from './Redux/taxItemSlice';

const namespace = 'features.settings.tax_items.index';

// Match TaxItem.external_tax_source? (IP-15103): a lone 0% QBO code such as
// "NO TAX SALES" must not flip Settings into external-only mode (IP-15194).
function effectiveRate(item) {
  if (item.rate != null && item.rate !== '') {
    return Number(item.rate);
  }
  if (Array.isArray(item.all_tax_rates)) {
    return item.all_tax_rates
      .filter((rate) => rate.active !== false && rate.hidden !== true)
      .reduce((total, rate) => total + parseFloat(rate.rate || 0), 0);
  }
  return 0;
}

function hasUsableExternalTaxSource(items) {
  return items.some((item) => item.external_id !== null && item.hidden === false && effectiveRate(item) !== 0);
}

function TaxItems({ taxItems, qbIntegrationEnabled }) {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.items.items);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [search, setSearch] = useState(null);
  const qbEnabled = qbIntegrationEnabled && hasUsableExternalTaxSource(items);

  useEffect(() => {
    setItems(taxItems);
  }, []);

  const setItems = (newItems) => {
    dispatch(updateItems(newItems));
  };

  const activeItems = useMemo(() => {
    return items.filter(
      (item) =>
        item.active === true && item.hidden === false && (!qbEnabled || (qbEnabled && item.external_id !== null))
    );
  }, [items, qbEnabled]);

  const visibleItems = useMemo(() => {
    return items.filter((item) => item.hidden === false && (!qbEnabled || (qbEnabled && item.external_id !== null)));
  }, [items, qbEnabled]);

  const filteredItems = useMemo(() => {
    if (search === null || search === undefined || search.trim() === '') {
      return visibleItems;
    }
    return visibleItems.filter((item) =>
      Object.values(item)
        .map((val) => (typeof val === 'string' ? val.toLowerCase() : val))
        .join(', ')
        .includes(search)
    );
  }, [visibleItems, search]);

  const onSwitch = (taxItem) => {
    return () =>
      api
        .patch(toggleActiveTaxItemPath(taxItem.id))
        .then((response) => {
          setItems(response.data.tax_items);
          toast.success(translate(taxItem.active ? 'success_deactivate' : 'success_activate', { namespace }));
        })
        .catch(() => {
          toast.error(translate('error_toggle_active', { namespace }));
        });
  };

  const onDelete = (taxItem) => {
    return () =>
      api
        .delete(taxItemPath(taxItem.id))
        .then((response) => {
          setItems(response.data.tax_items);
          toast.success(translate('success_delete', { namespace }));
        })
        .catch(() => {
          toast.error(translate('error_delete', { namespace }));
        });
  };

  const onSearchChange = (value) => {
    setSearch(value.toLowerCase());
  };

  const toggleExpandGroup = (groupId, event) => {
    if (
      event.target.tagName === 'A' ||
      event.target.tagName === 'BUTTON' ||
      event.target.closest('[data-allow-click]') !== null
    ) {
      return;
    }

    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const rowRenderer = ({ item }) =>
    expandedGroups[item.id] ? <ExpandableRow item={item} onSwitch={onSwitch} onDelete={onDelete} /> : null;

  const calculateGroupRate = (group) => {
    const num = group.all_tax_rates
      .filter((rate) => rate.active)
      .reduce((total, rate) => total + parseFloat(rate.rate), 0);
    return Number.isInteger(num) ? num.toFixed(1) : parseFloat(num.toFixed(5)).toString();
  };

  const columns = useMemo(
    () => [
      {
        label: translate('name', { namespace }),
        dataKey: 'name',
        renderer: ({ item }) => (
          <div
            style={
              /* remove style && change to <> if adding either button back */ {
                display: 'flex',
                height: '30px',
                alignItems: 'center'
              }
            }>
            {(item.external_id !== null || item.reference_id !== null || !item.active) && <span>{item.name}</span>}
            {item.external_id === null &&
              item.reference_id === null &&
              item.active === true &&
              item.type === 'TaxRate' && <EditTaxRate taxItem={item} namespace={namespace} />}
            {item.external_id === null &&
              item.reference_id === null &&
              item.active === true &&
              item.type === 'TaxGroup' && (
                <EditTaxGroup activeItems={activeItems} taxItem={item} namespace={namespace} />
              )}
            {item.type === 'TaxGroup' && (
              <i
                className={`fa fa-light pl-2 ${expandedGroups[item.id] ? 'fa-angle-up' : 'fa-angle-down'}`}
                style={{ cursor: 'pointer' }}
              />
            )}
            {item.external_id !== null && qbEnabled && (
              <div className="inline">
                &nbsp;&nbsp;
                <img
                  height="16"
                  width="16"
                  src="/qbo.svg"
                  className="custom-icon--qbo"
                  title={translate('qb_icon', { namespace })}
                  alt={translate('qb_icon', { namespace })}
                />
              </div>
            )}
          </div>
        )
      },
      {
        label: translate('agency', { namespace }),
        dataKey: 'agency',
        renderer: ({ item }) => item.tax_agency
      },
      {
        label: translate('rate', { namespace }),
        dataKey: 'rate',
        renderer: ({ item }) => `${item.type === 'TaxGroup' ? calculateGroupRate(item) : item.rate}%`
      },
      {
        label: translate('actions', { namespace }),
        align: 'left',
        dataKey: 'id',
        width: 70,
        renderer: ({ item }) => <ActionButtons taxItem={item} onSwitch={onSwitch(item)} onDelete={onDelete(item)} />
      }
    ],
    [items, expandedGroups]
  );

  return (
    <div className="workspace__body">
      <div className="workspace__major--listing">
        <div className="workspace__header">
          <div className="header__main">
            <h1 className="workspace__title">{translate('title', { namespace })}</h1>
          </div>
        </div>

        <div className="qmb-content-actions">
          {!qbEnabled && <CreateTaxItem activeItems={activeItems} namespace={namespace} />}
          {/* ARCHIVED RATES BUTTON - UNCOMMENT TO RE-ADDING
          <Brushaway
            anchor={archivedAnchor}
            title="Archived Rates"
            minWidth={800}>
            {(filteredInactiveItems.length > 0 || inactiveSearch?.length > 0) && (
              <div className="width-50 pull-right py-2 pr-1">
                <SearchPanel
                  placeholder={translate('search', { namespace: 'filters' })}
                  onChange={onInactiveSearchChange}
                />
              </div>
            )}
            {filteredInactiveItems.length === 0 && (
              <div style={{ textAlign: 'center', marginTop: '25px', color: '#1967D2' }}>
                {translate('no_data', { namespace })}
              </div>
            )}
            {filteredInactiveItems.length > 0 && (
              <Table columns={columns} data={filteredInactiveItems} size={TableSize.Full} variant="listing" hover />
            )}
          </Brushaway>
          */}

          {(filteredItems.length > 0 || search?.length > 0) && (
            <div className="content-actions__group" style={{ marginLeft: 'auto' }}>
              <div>
                <SearchPanel placeholder={translate('search', { namespace: 'filters' })} onChange={onSearchChange} />
              </div>
            </div>
          )}
        </div>
        <div>
          {filteredItems.length === 0 && (
            <div style={{ textAlign: 'center', marginTop: '25px', color: '#1967D2' }}>
              {translate('no_data', { namespace })}
            </div>
          )}
          {filteredItems.length > 0 && (
            <Table
              columns={columns}
              data={filteredItems}
              size={TableSize.Full}
              variant="listing"
              expandableRowRenderer={rowRenderer}
              onRowClick={({ item }, event) => {
                if (item.type === 'TaxGroup') toggleExpandGroup(item.id, event);
              }}
              expandedRows={expandedGroups}
              hover
            />
          )}
        </div>
      </div>
    </div>
  );
}

TaxItems.propTypes = {
  taxItems: PropTypes.array.isRequired,
  qbIntegrationEnabled: PropTypes.bool
};

export default TaxItems;
